import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getMessageByKey } from 'Stores/LanguageStore';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

/**
 * Restrictions the customer is told about. These are the only ones with configurable, customer
 * facing wording — everything in `StaffOnlyRestriction` already has its own customer-facing box.
 */
export type StateOfBooking = 'checked-in' | 'invoiced' | 'closed' | 'standard' | 'driverless_restriction';

/**
 * Restrictions that silently remove `Edit this booking` / `Cancel this booking` for staff. The
 * customer either can't reach the booking in that state or is already told about it elsewhere (the
 * `Payment pending` box, the migrated-booking notice), so these are surfaced to staff only.
 */
export type StaffOnlyRestriction = 'freight' | 'migrated' | 'unpaid' | 'check-in-finalised' | 'no-booking-permission';

export type BookingRestriction = Exclude<StateOfBooking, 'standard'> | StaffOnlyRestriction;

type BlockedAction = 'edit' | 'cancel';

interface StaffRestriction {
	/** The staff actions this restriction removes. Empty means staff can still edit and cancel. */
	blocks: BlockedAction[];
	/** Short sentence-case phrase for the reason column. Kept to one line where possible. */
	reason: string;
	/** Lower-case fragment used when several reasons are joined onto the customer row. */
	shortReason: string;
	/** Whether the restriction also stops the customer modifying the booking online. */
	locksCustomer: boolean;
}

/**
 * Staff copy is deliberately hardcoded — it is internal wording that explains the restriction, so
 * it is not part of the configurable (customer-facing) language pack.
 *
 * `blocks` has to match the real gates, which differ per reason. `canModifyBooking` returns early
 * for staff, so invoiced and closed only stop the *customer* from self-serving. Driverless, freight,
 * migrated and unpaid all fail `canEditBooking` before that early return, so they block editing for
 * everyone. Checked-in blocks cancelling for everyone via the second `canCancelBooking` in
 * BookingActionButtons, which gates the cancel action on top of `canModifyBooking` with no staff
 * bypass. Finalised check-in is the one restriction that blocks both actions for everyone.
 */
function getStaffRestriction(restriction: BookingRestriction, checkInStatus: string): StaffRestriction | null {
	const status = checkInStatus?.toLowerCase();
	switch (restriction) {
		case 'checked-in':
			return {
				blocks: ['cancel'],
				reason: `Already ${status}`,
				shortReason: `already ${status}`,
				locksCustomer: true,
			};
		case 'invoiced':
			return { blocks: [], reason: 'Invoiced', shortReason: 'invoiced', locksCustomer: true };
		case 'closed':
			return {
				blocks: [],
				reason: 'Trip closed for bookings',
				shortReason: 'trip closed for bookings',
				locksCustomer: true,
			};
		case 'driverless_restriction':
			return {
				blocks: ['edit'],
				reason: 'Driverless vehicle — cancel and re-book',
				shortReason: 'driverless vehicle',
				locksCustomer: true,
			};
		case 'freight':
			return { blocks: ['edit'], reason: 'Freight booking', shortReason: 'freight booking', locksCustomer: true };
		case 'migrated':
			return {
				blocks: ['edit'],
				reason: 'Migrated booking — changes are turned off',
				shortReason: 'migrated booking',
				locksCustomer: true,
			};
		case 'unpaid':
			return {
				blocks: ['edit'],
				reason: 'Payment not completed',
				shortReason: 'payment not completed',
				locksCustomer: true,
			};
		case 'check-in-finalised':
			return {
				blocks: ['edit', 'cancel'],
				reason: 'Check-in finalised — un-finalise to make changes',
				shortReason: 'check-in finalised',
				locksCustomer: true,
			};
		case 'no-booking-permission':
			return {
				blocks: ['edit'],
				reason: "You don't have 'Book on behalf of customer'",
				shortReason: 'no permission to book',
				locksCustomer: false,
			};
		default:
			return null;
	}
}

/**
 * Customer copy is configurable per language. Each reason is a standalone sentence so it reads the
 * same on its own or as one bullet of several; the "reach out to our staff" line is rendered once
 * as a footer rather than being repeated inside every reason.
 */
function getCustomerReason(
	stateOfBooking: StateOfBooking,
	checkInStatus: string,
	isOnlyRestriction: boolean,
): React.ReactNode {
	switch (stateOfBooking) {
		case 'checked-in':
			return (
				<FormattedMessage
					id="booking-information-box__reason__checked-in"
					defaultMessage="This booking has already been {checkInStatus}, so it can no longer be changed online."
					values={{ checkInStatus: checkInStatus?.toLowerCase() }}
				/>
			);
		case 'invoiced':
			return getMessageByKey(
				'booking-information-box__reason__invoiced',
				'This booking has been invoiced, so it can no longer be changed online.',
			);
		case 'closed':
			return getMessageByKey(
				'booking-information-box__reason__closed',
				'The ferry trip has closed for bookings, so this booking can no longer be changed online.',
			);
		case 'driverless_restriction': {
			const reason = getMessageByKey(
				'booking-information-box__reason__driverless-restriction',
				'This is a driverless vehicle booking and cannot be edited.',
			);
			// Only tell them to cancel and re-book when cancelling is actually open to them — any
			// second restriction (checked in, invoiced, closed) blocks the cancel as well.
			if (!isOnlyRestriction) {
				return reason;
			}
			return `${reason} ${getMessageByKey(
				'booking-information-box__driverless-restriction__advice',
				'To make changes, cancel the booking and book again.',
			)}`;
		}
		default:
			return null;
	}
}

/** The lost-action label for a row, e.g. `Can't edit or cancel`. */
function blockedLabel(blocks: BlockedAction[]): string {
	return `Can't ${blocks.join(' or ')}`;
}

/**
 * Staff version of the restriction box. Answers two questions — what the staff member themselves
 * can no longer do (the one they're asking when an action has vanished from `Manage booking`), and
 * what the customer can't do online (the one they get asked over the phone).
 *
 * Laid out as a two-column grid rather than headed groups: the left column names the lost action,
 * the right column gives the reason. Alignment carries the structure, so nothing inside the box is
 * bolded and the whole thing fits in as many lines as there are distinct restrictions.
 *
 * Restrictions blocking the same action share one row, and the customer's reasons are joined onto a
 * single row — the effect on them is always the same (locked out of online changes), only the
 * reasons differ.
 */
export function GetStaffRestrictionsBox(
	restrictions: BookingRestriction[],
	checkInStatus: string,
	alterationRulesOff: boolean,
	cancellationRulesOff: boolean,
) {
	const resolved = restrictions
		.map(restriction => getStaffRestriction(restriction, checkInStatus))
		.filter((detail): detail is StaffRestriction => isNotNullOrUndefined(detail));

	// Informational only — neither staff nor the customer lose an action.
	const feeWaiver = getFeeWaiverMessage(alterationRulesOff, cancellationRulesOff);

	if (resolved.length === 0 && feeWaiver === null) {
		return <></>;
	}

	const staffBlocked = resolved.filter(x => x.blocks.length > 0);
	const customerOnly = resolved.filter(x => x.blocks.length === 0 && x.locksCustomer);

	// Merge restrictions that take away the same action(s) so the label isn't repeated down the
	// column — "Can't edit | Freight booking · Payment not completed" rather than two rows.
	const rows: { key: string; label: string; reason: string }[] = [];
	staffBlocked.forEach(x => {
		const label = blockedLabel(x.blocks);
		const existing = rows.find(row => row.label === label);
		if (existing) {
			existing.reason += ` · ${x.reason}`;
		} else {
			rows.push({ key: label, label, reason: x.reason });
		}
	});

	if (customerOnly.length > 0) {
		rows.push({
			key: 'customer',
			label: 'Customer',
			reason: `Can't modify online: ${customerOnly.map(x => x.shortReason).join(', ')}`,
		});
	} else if (staffBlocked.some(x => x.locksCustomer)) {
		// Nothing extra locks the customer out, but the rows above do — say so rather than leaving
		// staff to work out whether the customer is stuck too.
		rows.push({
			key: 'customer',
			label: 'Customer',
			reason: `Can't modify online while ${staffBlocked.length === 1 ? 'this applies' : 'these apply'}`,
		});
	}

	if (feeWaiver !== null) {
		rows.push({ key: 'fees', label: 'Fees', reason: feeWaiver });
	}

	return (
		<MessageBox title="Booking restrictions" status="info">
			{
				<div className="message-box__restriction-grid">
					{rows.map(row => (
						<React.Fragment key={row.key}>
							<span className="message-box__restriction-grid__label">{row.label}</span>
							<span>{row.reason}</span>
						</React.Fragment>
					))}
				</div>
			}
		</MessageBox>
	);
}

/**
 * Customer version of the restriction box. Every restriction that applies is listed, rather than
 * only the first — a checked-in driverless vehicle booking can be neither cancelled nor edited, and
 * naming only one of those leaves the other a mystery.
 */
export function GetInformationBox(statesOfBooking: StateOfBooking[], checkInStatus: string) {
	const states = statesOfBooking.filter(state => state !== 'standard');

	const reasons = states
		.map(state => ({
			state,
			reason: getCustomerReason(state, checkInStatus, states.length === 1),
		}))
		.filter(x => isNotNullOrUndefined(x.reason));

	if (reasons.length === 0) {
		return <></>;
	}

	return (
		<MessageBox
			title={getMessageByKey('booking-information-box__header__default', 'Booking is closed for modifications')}
			status="info"
		>
			{
				<>
					{reasons.length === 1 ? (
						<p>{reasons[0].reason}</p>
					) : (
						<ul className="message-box__reason-list">
							{reasons.map(x => (
								<li key={x.state}>{x.reason}</li>
							))}
						</ul>
					)}
					<p>
						{getMessageByKey(
							'booking-information-box__footer',
							'If you think there is an error, please reach out to our staff for assistance.',
						)}
					</p>
				</>
			}
		</MessageBox>
	);
}

/**
 * The sentence saying the booking's ferry trip has alteration and/or cancellation rules turned off
 * (#2669, #2764), so changing or cancelling it won't charge those fees. Only this leg's ferry trip is
 * checked — moving onto a trip with alteration rules off is also free, but that trip isn't known
 * until it is chosen.
 */
function getFeeWaiverMessage(alterationRulesOff: boolean, cancellationRulesOff: boolean): string | null {
	if (alterationRulesOff && cancellationRulesOff) {
		return getMessageByKey(
			'booking-fee-waiver-box__alteration-and-cancellation',
			'Alteration and cancellation fees do not apply to this ferry trip.',
		);
	}
	if (alterationRulesOff) {
		return getMessageByKey('booking-fee-waiver-box__alteration', 'Alteration fees do not apply to this ferry trip.');
	}
	if (cancellationRulesOff) {
		return getMessageByKey('booking-fee-waiver-box__cancellation', 'Cancellation fees do not apply to this ferry trip.');
	}
	return null;
}

/** Customer version of the fee waiver notice. Staff get the same sentence as a row of their restrictions box. */
export function GetFeeWaiverBox(alterationRulesOff: boolean, cancellationRulesOff: boolean) {
	const message = getFeeWaiverMessage(alterationRulesOff, cancellationRulesOff);
	if (message === null) {
		return <></>;
	}
	return <MessageBox status="info">{message}</MessageBox>;
}
